/* eslint-disable @typescript-eslint/no-unused-vars */
import { iReviewFormValues } from '@services/ReviewService';
import { Field, getIn, useFormikContext } from 'formik';
import { ReactElement, useState } from 'react';
import gridStyles from '@pages/forms/Grid.module.scss';
import { ABNACNField, AddressFieldAccessible, ContactInputWithExperian, EmailFieldWithExperian, Modal, ReviewAddressModal, TextInput, TileRadioAccessible } from '@components';
import { tile } from '@interfaces/interfaces';
import { INVALID_NUMERIC_FORMAT } from '@services/ErrorMessages';
import { reviewInfoDisplayObject } from '@services/FormFieldConfig';
import { FormType } from '@constants/forms';
import styles from './CompanyFormReview.module.scss';

function CompanyFormReview(): ReactElement {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
    const { values, touched }: { values: iReviewFormValues; touched: any } = useFormikContext();
    const [isDisabled, setDisabled] = useState(false);
    const formikContext = useFormikContext();
    const pageFields = reviewInfoDisplayObject.pages.CompanyContactInformation.fields;
    const pageTitles = reviewInfoDisplayObject.pages.CompanyContactInformation;
    const [modal, setModal] = useState({
      show: false,
      title: '',
      content: <></>,
      contentClass: '',
    });
    const Abn = (): ReactElement => (
      <>
        <div>ABN</div>
      </>
    );
    const Acn = (): ReactElement => (
      <>
        <div>ACN</div>
      </>
    );
    const ABNACN_TILE_DATA: tile[] = [
      {
        body: <Acn />,
        value: 'ACN',
      },
      {
        body: <Abn />,
        value: 'ABN',
      },
    ];
    const YesValue = (): ReactElement => (
      <>
        <div>Yes</div>
      </>
    );
    const NoValue = (): ReactElement => (
      <>
        <div>No</div>
      </>
    );
    const differentToPostal: tile[] = [
      {
        body: <YesValue />,
        value: 'true',
      },
      {
        body: <NoValue />,
        value: 'false',
      },
    ];
    return (
      <>
        <span className="vfH2">{pageTitles.pageTitle}</span>
        <p>We need <b>your</b> details here, as the company's representative.</p>
        <div className={gridStyles.form}>
          <div className={gridStyles.gridColFull}>
            <Field component={TextInput} name={pageFields.givenName.formikName} title={pageFields.givenName.title} isMandatory />
          </div>
          <div className={gridStyles.gridColFull}>
            <Field component={TextInput} name={pageFields.familyName.formikName} title={pageFields.familyName.title} isMandatory />
          </div>
          <div className={gridStyles.gridColFull}>
            <Field component={TextInput} name={pageFields.positionInCompany.formikName} title={pageFields.positionInCompany.title} isMandatory />
          </div>
          <div className={gridStyles.gridColFull}>
            <Field component={TextInput} name={pageFields.companyName.formikName} title={pageFields.companyName.title} isMandatory />
          </div>
          <div className={gridStyles.gridColFull}>
            <Field
              component={TileRadioAccessible}
              name={pageFields.acnAbnType.formikName}
              title={pageFields.acnAbnType.title}
              tileData={ABNACN_TILE_DATA}
              orientationRow
              isMandatory
              tileChanged={() => {
                formikContext.setFieldValue('ABNYourDetailsCompany', '');
                formikContext.setFieldTouched('ABNYourDetailsCompany', false);
                formikContext.setFieldError('ABNYourDetailsCompany', INVALID_NUMERIC_FORMAT);
                formikContext.setFieldValue('ACNYourDetailsCompany', '');
                formikContext.setFieldTouched('ACNYourDetailsCompany', false);
                formikContext.setFieldError('ACNYourDetailsCompany', INVALID_NUMERIC_FORMAT);
              }}
            />
          </div>
          {getIn(values, 'AbnAcnTypeYourDetailsCompany') === 'ABN'
          && (
            <Field
              component={ABNACNField}
              name={pageFields.abnNumber.formikName}
              title={pageFields.abnNumber.title}
              isMandatory
              ABNACNType={getIn(values, pageFields.acnAbnType.formikName)}
              ABNACNValidFieldName={pageFields.isABNValid.formikName}
            />
          )}
          {getIn(values, 'AbnAcnTypeYourDetailsCompany') === 'ACN'
          && (
            <Field
              component={ABNACNField}
              name={pageFields.acnNumber.formikName}
              title={pageFields.acnNumber.title}
              isMandatory
              ABNACNType={getIn(values, pageFields.acnAbnType.formikName)}
              ABNACNValidFieldName={pageFields.isACNValid.formikName}
            />
          )}
          <div className={gridStyles.gridColFull}>
            <AddressFieldAccessible name={pageFields.postalAddress.formikName} title={pageFields.postalAddress.title} manualAddressText="Enter address manually" isMandatory />
          </div>
          <div className={gridStyles.gridColFull}>
            <Field
              component={TileRadioAccessible}
              name={pageFields.isAddressSameAsPostal.formikName}
              title={pageFields.isAddressSameAsPostal.title}
              tileData={differentToPostal}
              tileContainer
              isMandatory
              orientationRow
              classname={styles.tile}
            />
          </div>
          <div className={gridStyles.gridColFull}>
            {(getIn(values, 'DifferentToPostalYourDetailsCompany') === 'true')
              && (
                <div className={gridStyles.gridColFull}>
                  <AddressFieldAccessible
                    name={pageFields.streetAddress.formikName}
                    title={pageFields.streetAddress.title}
                    manualAddressText="Enter address manually"
                    isMandatory
                    disabled={isDisabled}
                  />
                </div>
              )}
          </div>
          <Modal close={() => setModal({ ...modal, show: false })} {...modal} />
          <div>
            <button
              type="button"
              style={{ border: 'none', color: '#0072ce', backgroundColor: 'transparent', cursor: 'pointer', marginTop: '5px', textDecoration: 'underline' }}
              onClick={() => {
                  const modalTitle = 'Have you moved recently?';
                  setModal({
                    show: true,
                    title: modalTitle,
                    content: ReviewAddressModal(),
                    contentClass: '',
                  });
                }}>
              Have you moved recently?
            </button>
          </div>
          <div className={gridStyles.gridColFull}>
            <Field
              component={ContactInputWithExperian}
              name={pageFields.contactNumber.formikName}
              title="Contact phone number"
              isMandatory
              orientationRow
              obligationNumber={values.OBLIGATION_NUMBER}
              formName={FormType.REVIEW}
              secondaryContact={false}
              hasPrimaryNumberType={false}
              warningMessage="ContactNumberYourDetailsCompanyWarning"
            />
          </div>
          <div className={gridStyles.gridColFull}>
            <Field
              component={EmailFieldWithExperian}
              name={pageFields.emailAddress.formikName}
              title="Email"
              isMandatory
              orientationRow
              obligationNumber={values.OBLIGATION_NUMBER}
              formName={FormType.REVIEW}
              noEmailField={pageFields.noEmail.formikName}
              currentSP={pageFields.noEmail.title}
              warningMessage="EmailYourDetailsCompanyWarning"
            />
          </div>
        </div>
      </>
    );
}

export default CompanyFormReview;
